/**
 * PaddleTürkiye Centralized Email Template Utility
 * Brand Colors: Emerald Green #059669 / #10b981
 * All templates use getBrandEmailLayout master wrapper
 */

/**
 * Master brand email layout wrapper
 * @param {Object} params
 * @param {string} params.title - Email heading
 * @param {string} params.subtitle - Sub-heading below title
 * @param {string} params.contentHtml - Main body HTML
 * @param {string} [params.buttonText] - CTA button label
 * @param {string} [params.buttonUrl] - CTA button URL
 * @returns {string} Complete HTML email string
 */
export const getBrandEmailLayout = ({ title, subtitle, contentHtml, buttonText, buttonUrl }) => {
  const ctaButton = buttonText && buttonUrl
    ? `<div style="margin: 32px 0; text-align: center;">
        <a href="${buttonUrl}"
           target="_blank"
           rel="noopener noreferrer"
           style="background-color: #059669; color: #ffffff; padding: 14px 32px; text-decoration: none;
                  border-radius: 8px; font-weight: 700; display: inline-block; font-size: 16px;
                  letter-spacing: 0.3px; border: none; cursor: pointer;">
          ${buttonText}
        </a>
       </div>`
    : '';

  return `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f8fafc; font-family: Arial, Helvetica, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8fafc; padding: 32px 16px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0"
               style="max-width: 600px; width: 100%; background-color: #ffffff;
                      border-radius: 12px; overflow: hidden;
                      box-shadow: 0 4px 24px rgba(0,0,0,0.08);">

          <!-- Brand Header -->
          <tr>
            <td style="background-color: #059669; padding: 0;">
              <div style="height: 4px; background: linear-gradient(90deg, #059669 0%, #10b981 50%, #34d399 100%);"></div>
              <div style="padding: 28px 32px; background-color: #059669; text-align: center;">
                <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 800; letter-spacing: 0.5px;">
                  🏄 PaddleTürkiye
                </h1>
                <p style="margin: 6px 0 0 0; color: rgba(255,255,255,0.85); font-size: 13px; font-weight: 400;">
                  Türkiye'nin Paddle Topluluğu
                </p>
              </div>
            </td>
          </tr>

          <!-- Title Section -->
          <tr>
            <td style="padding: 32px 32px 0 32px; border-bottom: 1px solid #e2e8f0;">
              <h2 style="margin: 0 0 8px 0; color: #0f172a; font-size: 22px; font-weight: 700;">${title}</h2>
              ${subtitle ? `<p style="margin: 0 0 24px 0; color: #64748b; font-size: 14px;">${subtitle}</p>` : '<div style="margin-bottom: 24px;"></div>'}
            </td>
          </tr>

          <!-- Main Content -->
          <tr>
            <td style="padding: 28px 32px;">
              ${contentHtml}
              ${ctaButton}
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color: #f8fafc; border-top: 1px solid #e2e8f0; padding: 24px 32px; text-align: center;">
              <p style="margin: 0 0 6px 0; font-size: 12px; color: #94a3b8;">
                © ${new Date()?.getFullYear()} PaddleTürkiye. Tüm hakları saklıdır.
              </p>
              <p style="margin: 0; font-size: 12px; color: #94a3b8;">
                İletişim: <a href="mailto:info@paddleturkiye.com" style="color: #059669; text-decoration: none;">info@paddleturkiye.com</a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE A: Event Request Received
// ─────────────────────────────────────────────────────────────────────────────
export const getEventRequestReceivedEmail = ({ participantName, eventTitle, eventDate, eventLocation }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${participantName || 'Değerli Katılımcı'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Etkinlik rezervasyon talebiniz başarıyla alınmıştır. Ekibimiz talebinizi inceleyecek ve en kısa sürede size geri dönecektir.
    </p>
    <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #059669; font-size: 15px; font-weight: 700;">📋 Rezervasyon Detayları</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px; width: 40%;">Etkinlik:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventTitle || '-'}</td>
        </tr>
        ${eventDate ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Tarih:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventDate}</td>
        </tr>` : ''}
        ${eventLocation ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Konum:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventLocation}</td>
        </tr>` : ''}
        <tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Durum:</td>
          <td style="padding: 4px 0;"><span style="background-color: #fef3c7; color: #d97706; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">⏳ İnceleniyor</span></td>
        </tr>
      </table>
    </div>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Onay e-postası gönderildiğinde ödeme linkiniz de iletilecektir. Sorularınız için <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazabilirsiniz.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Rezervasyon Talebiniz Alındı',
    subtitle: 'Talebiniz inceleme sürecindedir.',
    contentHtml,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE B: Event Approved + Payment Link
// ─────────────────────────────────────────────────────────────────────────────
export const getEventApprovedPaymentLinkEmail = ({ participantName, eventTitle, eventDate, eventLocation, paymentLink, amount }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${participantName || 'Değerli Katılımcı'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Harika haber! Etkinlik rezervasyonunuz <strong style="color: #059669;">onaylandı</strong>. Katılımınızı tamamlamak için aşağıdaki güvenli ödeme butonuna tıklayın.
    </p>
    <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #059669; font-size: 15px; font-weight: 700;">✅ Onaylanan Rezervasyon</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px; width: 40%;">Etkinlik:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventTitle || 'PaddleTürkiye Etkinliği'}</td>
        </tr>
        ${eventDate ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Tarih:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventDate}</td>
        </tr>` : ''}
        ${eventLocation ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Konum:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventLocation}</td>
        </tr>` : ''}
        ${amount ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Tutar:</td>
          <td style="padding: 4px 0; color: #059669; font-size: 14px; font-weight: 700;">${amount}</td>
        </tr>` : ''}
      </table>
    </div>
    <div style="margin: 32px 0; text-align: center;">
      <a href="${paymentLink}" target="_blank" style="background-color: #059669; color: #ffffff; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block; font-size: 16px;">Ödemeyi Tamamla</a>
    </div>
    <p style="font-size: 12px; color: #64748b; margin-top: 25px; text-align: left;">
      Eğer yukarıdaki butona tıklayamıyorsanız, aşağıdaki bağlantıyı kopyalayıp tarayıcınıza yapıştırabilirsiniz:<br><br>
      <a href="${paymentLink}" target="_blank" style="color: #059669; word-break: break-all;">${paymentLink}</a>
    </p>
    <p style="color: #64748b; font-size: 13px; line-height: 1.6; margin: 20px 0 0 0;">
      ⚠️ Bu ödeme linki size özel oluşturulmuştur. Lütfen başkalarıyla paylaşmayın.<br>
      Sorun yaşarsanız <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazın.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Rezervasyonunuz Onaylandı!',
    subtitle: 'Ödemenizi tamamlayarak yerinizi garantileyin.',
    contentHtml,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE C: Store Order Confirmation
// ─────────────────────────────────────────────────────────────────────────────
export const getStoreOrderConfirmationEmail = ({ customerName, orderNumber, orderItems, totalAmount, shippingAddress }) => {
  const itemsHtml = Array.isArray(orderItems) && orderItems?.length > 0
    ? orderItems?.map(item => `
        <tr>
          <td style="padding: 8px 0; color: #334155; font-size: 14px; border-bottom: 1px solid #f1f5f9;">${item?.name || item?.title || 'Ürün'}</td>
          <td style="padding: 8px 0; color: #64748b; font-size: 14px; text-align: center; border-bottom: 1px solid #f1f5f9;">${item?.quantity || 1}</td>
          <td style="padding: 8px 0; color: #059669; font-size: 14px; text-align: right; font-weight: 600; border-bottom: 1px solid #f1f5f9;">${item?.price || '-'}</td>
        </tr>`)?.join('')
    : `<tr><td colspan="3" style="padding: 8px 0; color: #64748b; font-size: 14px;">Sipariş detayları yükleniyor...</td></tr>`;

  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${customerName || 'Değerli Müşterimiz'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Siparişiniz başarıyla alınmıştır. Siparişinizi hazırlıyor ve en kısa sürede kargoya veriyoruz.
    </p>
    ${orderNumber ? `<div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px 20px; margin: 0 0 20px 0; text-align: center;">
      <p style="margin: 0; color: #059669; font-size: 14px; font-weight: 700;">Sipariş No: <span style="font-size: 18px;">#${orderNumber}</span></p>
    </div>` : ''}
    <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #0f172a; font-size: 15px; font-weight: 700;">🛍️ Sipariş İçeriği</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        <thead>
          <tr>
            <th style="text-align: left; padding: 6px 0; color: #64748b; font-size: 12px; font-weight: 600; border-bottom: 2px solid #e2e8f0;">ÜRÜN</th>
            <th style="text-align: center; padding: 6px 0; color: #64748b; font-size: 12px; font-weight: 600; border-bottom: 2px solid #e2e8f0;">ADET</th>
            <th style="text-align: right; padding: 6px 0; color: #64748b; font-size: 12px; font-weight: 600; border-bottom: 2px solid #e2e8f0;">FİYAT</th>
          </tr>
        </thead>
        <tbody>${itemsHtml}</tbody>
        ${totalAmount ? `<tfoot>
          <tr>
            <td colspan="2" style="padding: 10px 0 0 0; color: #0f172a; font-size: 15px; font-weight: 700;">Toplam:</td>
            <td style="padding: 10px 0 0 0; color: #059669; font-size: 15px; font-weight: 700; text-align: right;">${totalAmount}</td>
          </tr>
        </tfoot>` : ''}
      </table>
    </div>
    ${shippingAddress ? `<p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0 0 12px 0;">
      📦 <strong>Teslimat Adresi:</strong> ${shippingAddress}
    </p>` : ''}
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Siparişinizle ilgili sorularınız için <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazabilirsiniz.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Siparişiniz Alındı! 🎉',
    subtitle: 'Siparişiniz işleme alınmıştır.',
    contentHtml,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE D: Password Reset
// ─────────────────────────────────────────────────────────────────────────────
export const getPasswordResetEmail = ({ userName, resetLink }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba ${userName ? `<strong>${userName}</strong>` : ''},
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      PaddleTürkiye hesabınız için şifre sıfırlama talebinde bulundunuz. Aşağıdaki butona tıklayarak yeni şifrenizi belirleyebilirsiniz.
    </p>
    <div style="background-color: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px 0;">
      <p style="margin: 0; color: #9a3412; font-size: 13px; line-height: 1.6;">
        ⚠️ Bu link <strong>24 saat</strong> geçerlidir. Eğer bu talebi siz yapmadıysanız bu e-postayı görmezden gelebilirsiniz.
      </p>
    </div>
    <p style="color: #64748b; font-size: 13px; line-height: 1.6; margin: 20px 0 0 0;">
      Butona tıklayamıyorsanız şu linki kopyalayıp tarayıcınıza yapıştırın:<br>
      <span style="word-break: break-all; color: #059669; font-size: 12px;">${resetLink || '#'}</span>
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Şifre Sıfırlama',
    subtitle: 'Hesabınıza erişimi yeniden kazanın.',
    contentHtml,
    buttonText: '🔐 Şifremi Sıfırla',
    buttonUrl: resetLink,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE E: Welcome / New User Registration
// ─────────────────────────────────────────────────────────────────────────────
export const getWelcomeRegisterEmail = ({ userName }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${userName || 'Değerli Üyemiz'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      PaddleTürkiye ailesine hoş geldiniz! 🎉 Hesabınız başarıyla oluşturuldu. Artık etkinliklere katılabilir, mağazamızdan alışveriş yapabilir ve toplulukla bağlantı kurabilirsiniz.
    </p>
    <div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #059669; font-size: 15px; font-weight: 700;">🏄 Neler Yapabilirsiniz?</h3>
      <ul style="margin: 0; padding-left: 20px; color: #334155; font-size: 14px; line-height: 2;">
        <li>Paddle etkinliklerine kayıt olun</li>
        <li>Ekipman ve aksesuarları mağazamızdan satın alın</li>
        <li>Spot rehberini keşfedin</li>
        <li>Topluluk üyeleriyle mesajlaşın</li>
      </ul>
    </div>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Sorularınız için <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazabilirsiniz.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Aramıza Hoş Geldiniz! 🏄',
    subtitle: 'PaddleTürkiye hesabınız hazır.',
    contentHtml,
    buttonText: '🌊 Keşfetmeye Başla',
    buttonUrl: 'https://paddleturkiye.com',
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE F: Profile Update Security Notification
// ─────────────────────────────────────────────────────────────────────────────
export const getProfileUpdateInfoEmail = ({ userName, updateDate, updateType }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${userName || 'Değerli Üyemiz'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      PaddleTürkiye hesabınızda profil bilgileriniz güncellendi. Bu değişikliği siz yaptıysanız herhangi bir işlem yapmanıza gerek yoktur.
    </p>
    <div style="background-color: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #d97706; font-size: 15px; font-weight: 700;">🔔 Güncelleme Detayı</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        ${updateType ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px; width: 40%;">Güncelleme Türü:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${updateType}</td>
        </tr>` : ''}
        ${updateDate ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Tarih:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${updateDate}</td>
        </tr>` : ''}
      </table>
    </div>
    <div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px 0;">
      <p style="margin: 0; color: #991b1b; font-size: 13px; line-height: 1.6;">
        ⚠️ <strong>Bu değişikliği siz yapmadıysanız</strong>, lütfen hemen şifrenizi sıfırlayın ve <a href="mailto:info@paddleturkiye.com" style="color: #dc2626;">info@paddleturkiye.com</a> adresine bildirin.
      </p>
    </div>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Hesap güvenliğiniz bizim için önemlidir. Şüpheli bir durum fark ederseniz derhal iletişime geçin.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Profil Bilgileri Güncellendi',
    subtitle: 'Hesabınızda bir değişiklik yapıldı.',
    contentHtml,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE G: Store Cargo Tracking
// ─────────────────────────────────────────────────────────────────────────────
export const getStoreCargoTrackingEmail = ({ customerName, orderNumber, cargoCompany, trackingNumber, trackingUrl, estimatedDelivery }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${customerName || 'Değerli Müşterimiz'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Siparişiniz kargoya verildi! 📦 Aşağıdaki bilgileri kullanarak siparişinizi takip edebilirsiniz.
    </p>
    ${orderNumber ? `<div style="background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px 20px; margin: 0 0 20px 0; text-align: center;">
      <p style="margin: 0; color: #059669; font-size: 14px; font-weight: 700;">Sipariş No: <span style="font-size: 18px;">#${orderNumber}</span></p>
    </div>` : ''}
    <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #0f172a; font-size: 15px; font-weight: 700;">🚚 Kargo Bilgileri</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        ${cargoCompany ? `<tr>
          <td style="padding: 6px 0; color: #64748b; font-size: 14px; width: 40%;">Kargo Firması:</td>
          <td style="padding: 6px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${cargoCompany}</td>
        </tr>` : ''}
        ${trackingNumber ? `<tr>
          <td style="padding: 6px 0; color: #64748b; font-size: 14px;">Takip No:</td>
          <td style="padding: 6px 0; color: #059669; font-size: 14px; font-weight: 700; font-family: monospace;">${trackingNumber}</td>
        </tr>` : ''}
        ${estimatedDelivery ? `<tr>
          <td style="padding: 6px 0; color: #64748b; font-size: 14px;">Tahmini Teslimat:</td>
          <td style="padding: 6px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${estimatedDelivery}</td>
        </tr>` : ''}
      </table>
    </div>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Siparişinizle ilgili sorularınız için <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazabilirsiniz.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Siparişiniz Kargoya Verildi! 🚚',
    subtitle: 'Paketiniz yola çıktı.',
    contentHtml,
    buttonText: trackingUrl ? '📦 Kargonu Takip Et' : undefined,
    buttonUrl: trackingUrl,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE H: Event Payment Success + Ticket
// ─────────────────────────────────────────────────────────────────────────────
export const getEventPaymentSuccessTicketEmail = ({ participantName, eventTitle, eventDate, eventLocation, ticketCode, rawTicketId, amount }) => {
  const formattedTicketCode = rawTicketId
    ? ('PT-' + String(rawTicketId)?.replace(/-/g, '')?.slice(-8)?.toUpperCase())
    : (ticketCode || 'PT-8F12A3B0');

  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${participantName || 'Değerli Katılımcı'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Ödemeniz başarıyla alındı! 🎉 Etkinlik biletiniz aşağıda yer almaktadır. Etkinlik günü bu bilgileri yanınızda bulundurun.
    </p>
    <div style="background-color: #f0fdf4; border: 2px solid #059669; border-radius: 12px; padding: 24px; margin: 0 0 20px 0; text-align: center;">
      <div style="font-size: 40px; margin-bottom: 8px;">🎫</div>
      <h3 style="margin: 0 0 4px 0; color: #059669; font-size: 18px; font-weight: 800;">ETKİNLİK BİLETİ</h3>
      <div style="background-color: #f0fdf4; border: 2px dashed #059669; border-radius: 12px; padding: 20px; margin: 25px 0; text-align: center;">
        <span style="font-size: 11px; font-weight: bold; color: #047857; text-transform: uppercase; letter-spacing: 1.5px; display: block; margin-bottom: 6px;">
          🎟️ DİJİTAL BİLET KODUNUZ
        </span>
        <div style="font-family: 'Courier New', Courier, monospace; font-size: 24px; font-weight: 800; color: #065f46; letter-spacing: 3px;">
          ${formattedTicketCode}
        </div>
        <span style="font-size: 11px; color: #059669; margin-top: 8px; display: block;">
          Etkinlik girişinde bu kodu veya e-posta onayınızı göstermeniz yeterlidir.
        </span>
      </div>
      <table cellpadding="0" cellspacing="0" width="100%" style="margin-top: 16px; text-align: left;">
        <tr>
          <td style="padding: 5px 0; color: #64748b; font-size: 14px; width: 40%;">Etkinlik:</td>
          <td style="padding: 5px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventTitle || '-'}</td>
        </tr>
        ${eventDate ? `<tr>
          <td style="padding: 5px 0; color: #64748b; font-size: 14px;">Tarih & Saat:</td>
          <td style="padding: 5px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventDate}</td>
        </tr>` : ''}
        ${eventLocation ? `<tr>
          <td style="padding: 5px 0; color: #64748b; font-size: 14px;">Konum:</td>
          <td style="padding: 5px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventLocation}</td>
        </tr>` : ''}
        ${amount ? `<tr>
          <td style="padding: 5px 0; color: #64748b; font-size: 14px;">Ödenen Tutar:</td>
          <td style="padding: 5px 0; color: #059669; font-size: 14px; font-weight: 700;">${amount}</td>
        </tr>` : ''}
        <tr>
          <td style="padding: 5px 0; color: #64748b; font-size: 14px;">Durum:</td>
          <td style="padding: 5px 0;"><span style="background-color: #d1fae5; color: #065f46; padding: 2px 10px; border-radius: 4px; font-size: 12px; font-weight: 700;">✅ ONAYLANDI</span></td>
        </tr>
      </table>
    </div>
    <div style="background-color: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px 0;">
      <p style="margin: 0; color: #1e40af; font-size: 13px; line-height: 1.7;">
        📌 <strong>Önemli Hatırlatma:</strong> Etkinlik günü bu e-postayı veya bilet kodunuzu organizatöre göstermeniz gerekmektedir. Lütfen zamanında gelmeyi unutmayın.
      </p>
    </div>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      Sorularınız için <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a> adresine yazabilirsiniz.
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Ödemeniz Alındı & Biletiniz Hazır! 🎫',
    subtitle: 'Etkinliğe katılımınız onaylandı.',
    contentHtml,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE I: Event Reservation Canceled (Admin-triggered)
// ─────────────────────────────────────────────────────────────────────────────
export const getEventCanceledEmail = ({ participantName, eventName, eventDate, eventLocation, cancellationReason }) => {
  const contentHtml = `
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 16px 0;">
      Merhaba <strong>${participantName || 'Değerli Katılımcı'}</strong>,
    </p>
    <p style="color: #334155; font-size: 16px; line-height: 1.7; margin: 0 0 20px 0;">
      Üzülerek bildiriyoruz ki <strong>${eventName || 'etkinlik'}</strong> için yapmış olduğunuz rezervasyon yönetici tarafından iptal edilmiştir.
    </p>
    <div style="background-color: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
      <h3 style="margin: 0 0 12px 0; color: #dc2626; font-size: 15px; font-weight: 700;">❌ İptal Edilen Rezervasyon</h3>
      <table cellpadding="0" cellspacing="0" width="100%">
        <tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px; width: 40%;">Etkinlik:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventName || '-'}</td>
        </tr>
        ${eventDate ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Tarih:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventDate}</td>
        </tr>` : ''}
        ${eventLocation ? `<tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Konum:</td>
          <td style="padding: 4px 0; color: #0f172a; font-size: 14px; font-weight: 600;">${eventLocation}</td>
        </tr>` : ''}
        <tr>
          <td style="padding: 4px 0; color: #64748b; font-size: 14px;">Durum:</td>
          <td style="padding: 4px 0;"><span style="background-color: #fee2e2; color: #dc2626; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600;">❌ İptal Edildi</span></td>
        </tr>
      </table>
    </div>
    ${cancellationReason ? `
    <div style="background-color: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px 0;">
      <p style="margin: 0 0 4px 0; color: #9a3412; font-size: 13px; font-weight: 700;">İptal Nedeni:</p>
      <p style="margin: 0; color: #7c2d12; font-size: 14px; line-height: 1.6;">${cancellationReason}</p>
    </div>` : ''}
    <p style="color: #334155; font-size: 14px; line-height: 1.7; margin: 0 0 16px 0;">
      Bu konuda sorularınız veya itirazlarınız varsa lütfen bizimle iletişime geçin. Anlayışınız için teşekkür ederiz.
    </p>
    <p style="color: #64748b; font-size: 14px; line-height: 1.6; margin: 0;">
      İletişim: <a href="mailto:info@paddleturkiye.com" style="color: #059669;">info@paddleturkiye.com</a>
    </p>
  `;

  return getBrandEmailLayout({
    title: 'Rezervasyonunuz İptal Edildi',
    subtitle: 'Rezervasyon iptali hakkında bilgilendirme.',
    contentHtml,
  });
};

export default {
  getBrandEmailLayout,
  getEventRequestReceivedEmail,
  getEventApprovedPaymentLinkEmail,
  getStoreOrderConfirmationEmail,
  getPasswordResetEmail,
  getWelcomeRegisterEmail,
  getProfileUpdateInfoEmail,
  getStoreCargoTrackingEmail,
  getEventPaymentSuccessTicketEmail,
  getEventCanceledEmail,
};
